import BoldText from '@/components/BoldText';
import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { OrderTagEnum, SETTING_TYPE, WIDGET_SETTING_CHOICE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { handleVisibleBanner, isOpenChangeFormatTagBannerSelector } from '@/redux/slice/banner.slice';
import { generalSettingSelector, handleSaveSettings } from '@/redux/slice/setting.slice';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import { BlockStack, Box, Card, Checkbox, Divider, InlineStack, Link, Tabs } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

type OrderOption = {
  id: string;
  title: string;
  helpText: string;
};

type OrderOptionsData = {
  shipping: OrderOption[];
  delivery: OrderOption[];
  pickup: OrderOption[];
};

export const OrderTag = () => {
  const { t } = useTranslation('general_settings');
  const generalSettings = useSelector(generalSettingSelector);
  const dispatch = useDispatch();

  const isOpenChangeFormatTagBanner = useSelector(isOpenChangeFormatTagBannerSelector);
  const navigate = useNavigate();

  const DEFAULT_ORDER_TAGS = useMemo(
    () => [
      OrderTagEnum.SHIPPING_NAME,
      OrderTagEnum.SHIPPING_DATE,
      OrderTagEnum.SHIPPING_TIME,
      OrderTagEnum.DELIVERY_NAME,
      OrderTagEnum.DELIVERY_DATE,
      OrderTagEnum.DELIVERY_TIME,
      OrderTagEnum.PICKUP_NAME,
      OrderTagEnum.PICKUP_DATE,
      OrderTagEnum.PICKUP_TIME,
    ],
    [],
  );

  const getUpdatedTags = useCallback(
    (currentTags: string[], tag: string) => {
      const hasTag = currentTags.includes(tag);
      const newTags = hasTag ? currentTags.filter((t) => t !== tag) : [...currentTags, tag];

      return DEFAULT_ORDER_TAGS.filter((t) => newTags.includes(t));
    },
    [DEFAULT_ORDER_TAGS],
  );

  const orderTagsMethod = useMemo(
    () => [
      {
        id: SETTING_TYPE.SHIPPING,
        content: t('sections.orderManagement.orderTag.method.0'),
        panelID: 'order-tag-shipping',
      },
      {
        id: SETTING_TYPE.DELIVERY,
        content: t('sections.orderManagement.orderTag.method.1'),
        panelID: 'order-tag-delivery',
      },
      {
        id: SETTING_TYPE.PICKUP,
        content: t('sections.orderManagement.orderTag.method.2'),
        panelID: 'order-tag-pickup',
      },
    ],
    [t],
  );

  const orderOptionsData = useMemo(
    () => ({
      shipping: [
        {
          id: '0',
          title: t('sections.orderManagement.orderTag.methodContent.shipping.0.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.shipping.0.helpText'),
          tag: OrderTagEnum.SHIPPING_NAME,
        },
        {
          id: '1',
          title: t('sections.orderManagement.orderTag.methodContent.shipping.1.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.shipping.1.helpText'),
          tag: OrderTagEnum.SHIPPING_DATE,
        },
        {
          id: '2',
          title: t('sections.orderManagement.orderTag.methodContent.shipping.2.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.shipping.2.helpText'),
          tag: OrderTagEnum.SHIPPING_TIME,
        },
      ],
      delivery: [
        {
          id: '0',
          title: t('sections.orderManagement.orderTag.methodContent.delivery.0.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.delivery.0.helpText'),
          tag: OrderTagEnum.DELIVERY_NAME,
        },
        {
          id: '1',
          title: t('sections.orderManagement.orderTag.methodContent.delivery.1.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.delivery.1.helpText'),
          tag: OrderTagEnum.DELIVERY_DATE,
        },
        {
          id: '2',
          title: t('sections.orderManagement.orderTag.methodContent.delivery.2.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.delivery.2.helpText'),
          tag: OrderTagEnum.DELIVERY_TIME,
        },
      ],
      pickup: [
        {
          id: '0',
          title: t('sections.orderManagement.orderTag.methodContent.pickup.0.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.pickup.0.helpText'),
          tag: OrderTagEnum.PICKUP_NAME,
        },
        {
          id: '1',
          title: t('sections.orderManagement.orderTag.methodContent.pickup.1.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.pickup.1.helpText'),
          tag: OrderTagEnum.PICKUP_DATE,
        },
        {
          id: '2',
          title: t('sections.orderManagement.orderTag.methodContent.pickup.2.title'),
          helpText: t('sections.orderManagement.orderTag.methodContent.pickup.2.helpText'),
          tag: OrderTagEnum.PICKUP_TIME,
        },
      ],
    }),
    [t],
  );

  const [selectTabMethod, setSelectTabMethod] = useState(orderTagsMethod[0].id);

  const selectedMethodOptions = orderOptionsData[selectTabMethod as keyof OrderOptionsData];

  const handleNavigate = () => {
    dispatch(handleLocationTab({ key: 'widgetTab', value: WIDGET_SETTING_CHOICE.DESIGN }));
    navigate(PATH.WIDGET, { state: { scrollToDateFormat: 'date-time-format-widget-appearance' } });
  };

  return (
    <Card padding={'300'}>
      <BlockStack gap={'300'}>
        <Box>
          <BoldText>{t('sections.orderManagement.orderTag.title')}</BoldText>
          <RegularText tone="subdued">{t('sections.orderManagement.orderTag.helpText')}</RegularText>
        </Box>
        <InformationBanner
          isVisible={isOpenChangeFormatTagBanner}
          onDismiss={() =>
            dispatch(
              handleVisibleBanner({
                key: 'isOpenChangeFormatTagBanner',
                value: false,
              }),
            )
          }
        >
          <RegularText>
            Want to change format tag? <Link onClick={handleNavigate}>Go there</Link>
          </RegularText>
        </InformationBanner>
        <Card padding={'300'}>
          <InlineStack align="space-between">
            <BoldText as="h3" fontWeight="medium">
              {t('sections.orderManagement.orderTag.automaticSwitchTitle')}
            </BoldText>
            <Switch
              isActive={generalSettings.isEnableOrderTag}
              onSwitch={() => dispatch(handleSaveSettings({ isEnableOrderTag: !generalSettings.isEnableOrderTag }))}
            />
          </InlineStack>
          <RegularText>
            Orders are tagged with the relevant <RegularText fontWeight="bold">delivery method, date, time, day</RegularText> and{' '}
            <RegularText fontWeight="bold">location.</RegularText>
          </RegularText>
          {generalSettings.isEnableOrderTag && (
            <>
              <Box paddingBlockStart={'500'} paddingBlockEnd={'200'}>
                <Divider />
              </Box>
              <Tabs
                tabs={orderTagsMethod}
                selected={orderTagsMethod.findIndex((tab) => tab.id === selectTabMethod)}
                onSelect={(index) => setSelectTabMethod(orderTagsMethod[index].id)}
                fitted
              />
              {selectedMethodOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  label={option.title}
                  helpText={option.helpText}
                  checked={generalSettings.orderTag.includes(option.tag)}
                  onChange={() =>
                    dispatch(
                      handleSaveSettings({
                        orderTag: getUpdatedTags(generalSettings.orderTag, option.tag),
                      }),
                    )
                  }
                />
              ))}
            </>
          )}
        </Card>
      </BlockStack>
    </Card>
  );
};
